import { Injectable, Injector, Renderer2 } from '@angular/core'
import { DynamicElementInterceptor, DynamicElementSpecification } from '@view-app/dynamic'

/**
 * Configurations for the {@link TableElementInterceptor}
 */
interface TableElementInterceptorConfig {
  defaultClasses: string[]
}

/**
 * An interceptor that will ensure that tables are wrapped with a div and the provided default classes.
 */
@Injectable({ providedIn: 'root' })
export class TableElementInterceptor implements DynamicElementInterceptor {

  /**
   * Hook to update the element after it's children have been built
   *
   * Wraps the original element and all it's children inside a responsive wrapper element.
   * Modifies the structure of the
   *
   * @param injector  The injector context for the dynamic element
   * @param _args     unused
   * @param element   The table element that will be wrapped
   * @param config    The configuration for the interceptor
   *
   * @returns       The responsive element that wraps the original table element
   */
  afterChildrenBuilt(injector: Injector, _args: DynamicElementSpecification, element: HTMLElement, config: TableElementInterceptorConfig) {
    let renderer2 = injector.get<Renderer2>(Renderer2)

    // Move any child rows orphaned from a table body
    let updatedElement = this.moveRowsIntoBody(renderer2, element)

    // Wrap and return the element
    return this.wrapElement(renderer2, updatedElement, config?.defaultClasses)
  }

  /**
   * Move any HTMLTableRowElements that are not within an existing HTMLTableHeadElement, HTMLTableBodyElement, or HTMLTableFootElement
   * into an HTMLTableBodyElement
   *
   * @param renderer2 The renderer
   * @param element   The HTMLTableElement that is the subject of the move
   *
   * @returns         The updated instance of the HTMLTableElement
   */
  moveRowsIntoBody(renderer2: Renderer2, element: HTMLElement): HTMLElement {
    // Collect the rows into the table body
    let tableBodyElement = renderer2.createElement('tbody')
    let tableFootElement

    Array.from(element.children).forEach(it => {
      if (['THEAD', 'TFOOT', 'TBODY'].includes(it.tagName)) {
        if (it.tagName === 'TFOOT') {
          tableFootElement = it
        }

        return
      }

      renderer2.appendChild(tableBodyElement, it)
    })

    // If a table foot exists place it before, else just append it to the original element
    if (tableFootElement) {
      renderer2.insertBefore(element, tableBodyElement, tableFootElement)
    }

    else {
      renderer2.appendChild(element, tableBodyElement)
    }

    return element
  }

  /**
   * Wrap the original HTMLTableElement with an HTMLDivElement.
   *
   * @param renderer2       The renderer
   * @param element         The original element that will be wrapped
   * @param defaultClasses  The classes that will be applied to the wrapper element
   *
   * @returns               The element that wraps the HTMLTableElement
   */
  wrapElement(renderer2: Renderer2, element: HTMLElement, defaultClasses: string[] = []): HTMLElement {
    let wrapperElement = renderer2.createElement('div')
    defaultClasses.forEach(it => renderer2.addClass(wrapperElement, it))
    renderer2.appendChild(wrapperElement, element)
    return wrapperElement
  }

}
